"use client";

import type { Gallery } from "@/lib/projects/types";
import classNames from "classnames";
import Image from "next/image";
import { useState } from "react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, type SwiperClass, SwiperSlide } from "swiper/react";
import styles from "./GallerySlider.module.css";

type Props = {
  gallery: Gallery[] | undefined;
};

export const GallerySlider = ({ gallery }: Props) => {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  // Состояние для отслеживания загрузки изображений
  const [loadedImages, setLoadedImages] = useState<boolean[]>([]);

  const handleSwiperInit = (swiper: SwiperClass) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const handleImageLoad = (index: number) => {
    // Обновляем состояние, отмечая, что изображение загружено
    setLoadedImages((prev) => {
      const newLoaded = [...prev];
      newLoaded[index] = true;
      return newLoaded;
    });
  };

  if (!gallery) {
    return null;
  }

  return (
    <section className={styles.gallery}>
      <Swiper
        navigation={{
          nextEl: `.${styles["arrow-button-next"]}`,
          prevEl: `.${styles["arrow-button-prev"]}`,
        }}
        centeredSlides={true}
        modules={[Navigation]}
        className={styles.swiper}
        onInit={handleSwiperInit}
        onSlideChange={handleSlideChange}
      >
        {gallery?.map((image, index) => {
          return (
            <SwiperSlide key={index}>
              <div className={styles.imageContainer}>
                {/* Показываем спиннер, пока изображение не загружено */}
                {!loadedImages[index] && (
                  <div className={styles.loader}>
                    <div className={styles.spinner} />
                  </div>
                )}
                <Image
                  className={styles.image}
                  width={1130}
                  height={560}
                  src={image}
                  loading="lazy"
                  alt={"Фото галереи"}
                  onLoad={() => handleImageLoad(index)}
                  // Скрываем изображение, пока оно не загружено
                  style={{ opacity: loadedImages[index] ? 1 : 0 }}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className={styles["slider-controls"]}>
        <ArrowButton left={true} disabled={isBeginning} />
        <ArrowButton disabled={isEnd} />
      </div>
    </section>
  );
};

function ArrowButton({
  left,
  disabled,
}: {
  left?: boolean;
  disabled?: boolean;
}) {
  return (
    <svg
      role="img"
      aria-label={left ? "Кнопка предыдущий слайд" : "Кнопка следующий слайд"}
      className={classNames({
        [styles["arrow-button-prev"]]: left,
        [styles["arrow-button-next"]]: !left,
        [styles.disabled]: disabled,
      })}
      xmlns="http://www.w3.org/2000/svg"
      width="50"
      height="50"
      viewBox="0 0 50 50"
      fill="none"
      style={{ rotate: left ? "" : "-180deg" }}
    >
      <rect width="50" height="50" rx="25" fill="#F7FBFC" />
      <path
        d="M28.3777 13.0747L31.5371 16.2569L23.2165 24.6132L31.5371 32.9694L28.3777 36.1516L16.9217 24.6132L28.3777 13.0747Z"
        fill="#769FCD"
      />
    </svg>
  );
}
